import { animation } from '@design-system/styles'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'
import { isTablet } from 'react-device-detect'
import { useHistory, useRouteMatch } from 'react-router'

import { Badge } from '@/component/atoms/Badge'
import { Icon } from '@/component/atoms/Icon'
import EarlybirdBanner from '@/component/molecules/EarlybirdBanner/EarlybirdBanner'
import NotificationService from '@/contentComponents/global/Notification/Notification.service'
import useRepository from '@/hooks/useRepository'
import { podMallCartService } from '@/page/PODMall/pages/PODMallCart/_service/PODMallCart.service'
import PODMallCouponService from '@/page/PODMall/pages/PODMallManage/PODMallCoupon/_service/PODMallCoupon.service'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import HeaderMenuPopover from './HeaderMenuPopover'
import S from './HeaderUtil.style'
import Profile from './Profile'
import RefreshButton from './RefreshButton'

const HeaderUtil = () => {
  const notificationService = useRepository(NotificationService)
  const podCouponService = useRepository(PODMallCouponService)
  const history = useHistory()

  const [beforeUnreadCount, setBeforeUnreadCount] = useState(Number.MAX_SAFE_INTEGER)
  const [ringAnimation, setRingAnimation] = useState(false)

  const ref = useRef<NodeJS.Timeout | null>(null)

  const isGuidePage = useRouteMatch(RouteName.guide)

  useEffect(() => {
    setBeforeUnreadCount(notificationService.unreadCount)
    if (beforeUnreadCount !== -1 && beforeUnreadCount < notificationService.unreadCount) {
      setRingAnimation(true)
      return
    }

    ref.current = setTimeout(() => {
      setRingAnimation(false)
      ref.current = null
    }, 1500)
  }, [notificationService.unreadCount])

  useEffect(() => {
    return () => {
      if (ref.current) {
        clearTimeout(ref.current)
      }
    }
  }, [])

  useEffect(() => {
    podMallCartService.fetchSetCartItems()
    podCouponService.fetchSetCouponList()
  }, [])

  return (
    <div css={S.Wrap}>
      {/* 노출 조건(잉글리시플랫 권한·스쿨플랫·설탭)은 배너가 스스로 판정한다. */}
      <EarlybirdBanner />
      {commonRepo.isSchoolflat && (
        <button
          css={S.guideButton}
          onClick={() => {
            mixpanelClient.track('GNB_스쿨플랫가이드_click')
            history.push(RouteName.guide)
          }}
        >
          <Badge
            size="m"
            radius="200"
            {...(isGuidePage
              ? {
                  color: 'var(--Colors-Slate-600)',
                  backgroundColor: 'var(--Colors-Slate-300)',
                  borderColor: 'var(--Colors-Slate-400)',
                }
              : {
                  color: 'var(--Colors-Slate-500)',
                  backgroundColor: 'var(--Colors-Slate-100)',
                  borderColor: 'var(--Colors-Slate-300)',
                })}
          >
            스쿨플랫 가이드
          </Badge>
        </button>
      )}
      {/* PC 웹은 브라우저 자체 새로고침이 있어 태블릿/일렉트론에서만 노출 */}
      {(isTablet || commonRepo.isElectronApp) && <RefreshButton />}
      {commonRepo.permission.get.orderManage.access && (
        <div
          style={{
            position: 'relative',
          }}
        >
          <HeaderMenuPopover />
        </div>
      )}
      {commonRepo.permission.get.notification.access && (
        <button
          className="flex handler-button"
          onClick={() => {
            notificationService.open()
            mixpanelClient.track('T11-01_GNB_알림아이콘btn')
          }}
        >
          <div className="icon__wrapper">
            <Icon name="notification" size={20} color="var(--Colors-Gray-700)" />
            {notificationService.unreadCount > 0 && (
              <p
                className={clsx(
                  'count-badge',
                  'noti-badge',
                  notificationService.unreadCount >= 99 && 'max-count',
                )}
                css={ringAnimation && animation.pulse}
              >
                {notificationService.unreadCountWithLimit}
              </p>
            )}
          </div>
        </button>
      )}
      {commonRepo.permission.get.myinfo.access && (
        <div css={S.basic} data-component="유저페이지 이동">
          <Profile />
        </div>
      )}
    </div>
  )
}

export default observer(HeaderUtil)
